import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, type ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";
import type { AxiosResponse } from "axios";

import type {
  DustClass,
  DustClassNew,
  DustClassQuery,
  DustClassRescheduleCandidate,
  DustClassRescheduleRequest,
  DustClassRescheduleResult,
  DustClassRescheduleTarget,
} from "./dustClass";
import dustClassService from "./dustClassService";

export type DustClassByIdConfig = PagingApiAndNotifierConfig & {
  expand?: string[];
};

const URL = "dust-classes";
const ENTITY_NAME = "Dust Class";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<DustClass>(apiHosts.AQMS, axiosParams);

const getDustClasses = (params: DustClassQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getNewDustClass = async () => {
  return {
    data: dustClassService.getInitialData(),
  } as AxiosResponse<DustClassNew, any>;
};

const getDustClassById = (id: string | number, config: DustClassByIdConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    params: config.expand !== undefined ? { expand: config.expand } : undefined,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const createDustClass = (dustClass: DustClass, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.post(URL, dustClass, {
    successToastCb,
    errorToastCb,
  });
};

const updateDustClass = (dustClass: DustClass, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.put(`${URL}/${dustClass.id}`, dustClass, {
    successToastCb,
    errorToastCb,
  });
};

const saveDustClass = (dustClass: DustClass, config: PagingApiAndNotifierConfig = {}) => {
  if (dustClass.id) {
    return updateDustClass(dustClass, config);
  }
  return createDustClass(dustClass, config);
};

export type IssueDustCardsResponse = {
  issued: number;
  revoked: number;
  dustClass: DustClass;
};

const issueCardsApi = apiService<IssueDustCardsResponse>(apiHosts.AQMS, axiosParams);
const rescheduleApi = apiService<DustClassRescheduleResult>(apiHosts.AQMS, axiosParams);
const rescheduleCandidatesApi = apiService<{ candidates: DustClassRescheduleCandidate[] }>(apiHosts.AQMS, axiosParams);
const rescheduleTargetsApi = apiService<{ targets: DustClassRescheduleTarget[] }>(apiHosts.AQMS, axiosParams);

const issueDustCards = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = "Dust Cards";
    config?.errorToastCb?.(options);
  };
  return issueCardsApi.post(`${URL}/${id}/issue-cards`, {}, { successToastCb, errorToastCb });
};

const getDustClassRescheduleCandidates = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = "Eligible Attendees";
    config?.errorToastCb?.(options);
  };
  return rescheduleCandidatesApi.get(`${URL}/${id}/reschedule-candidates`, {
    successToastCb,
    errorToastCb,
  });
};

const getDustClassRescheduleTargets = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = "Available Dust Classes";
    config?.errorToastCb?.(options);
  };
  return rescheduleTargetsApi.get(`${URL}/${id}/reschedule-targets`, {
    successToastCb,
    errorToastCb,
  });
};

const rescheduleDustClassAttendees = (
  id: string | number,
  request: DustClassRescheduleRequest,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = "Dust Class Attendees";
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = "Dust Class Attendees";
    config?.errorToastCb?.(options);
  };
  return rescheduleApi.post(`${URL}/${id}/reschedule-attendees`, request, { successToastCb, errorToastCb });
};

const deleteDustClassById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  return api.delete(`${URL}/${id}`, {
    successToastCb,
    errorToastCb,
  });
};

export {
  getDustClasses,
  getNewDustClass,
  getDustClassById,
  createDustClass,
  updateDustClass,
  saveDustClass,
  deleteDustClassById,
  issueDustCards,
  getDustClassRescheduleCandidates,
  getDustClassRescheduleTargets,
  rescheduleDustClassAttendees,
};
